/**
 * @file Specialties hook — manages a professional's specialties and cedulas.
 *
 * Each specialty has a name (e.g. "Cardiologia") and a cedula number.
 * Provides CRUD operations with optimistic local state updates.
 *
 * @module hooks/useSpecialties
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { professionalService } from "../services";

/**
 * Hook for managing the current professional's specialties.
 * @returns {{ specialties: Object[], isLoading: boolean, addSpecialty: Function, updateSpecialty: Function, removeSpecialty: Function, refresh: Function }}
 */
export function useSpecialties() {
  const [specialties, setSpecialties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await professionalService.getSpecialties();
      setSpecialties(res.data || []);
    } catch {
      // handled by interceptor
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const addSpecialty = async (data) => {
    const res = await professionalService.addSpecialty(data);
    setSpecialties((prev) => [...prev, res.data]);
    toast.success("Especialidad agregada");
    return res.data;
  };

  const updateSpecialty = async (id, data) => {
    const res = await professionalService.updateSpecialty(id, data);
    setSpecialties((prev) => prev.map((s) => (s.id === id ? res.data : s)));
    toast.success("Especialidad actualizada");
    return res.data;
  };

  const removeSpecialty = async (id) => {
    await professionalService.removeSpecialty(id);
    setSpecialties((prev) => prev.filter((s) => s.id !== id));
    toast.success("Especialidad eliminada");
  };

  return { specialties, isLoading, addSpecialty, updateSpecialty, removeSpecialty, refresh: fetch };
}
